import { createLogger } from "@/shared/logging";
import {
  enabledAuthProviders,
  type AuthSession,
  type EnabledAuthProviderKind,
} from "../domain/authSession";

// Rerun 새 창 뷰어 껍데기(BE `packages/spnp/viewer-shell`)도 이 키의 `accessToken` 을 읽는다. 키나 모양을
// 바꾸면 껍데기도 함께 바꾼다.
const authSessionStorageKey = "spawning-pool:auth:session";

// 토큰이 요청 중 만료되는 race를 피하기 위해 약간의 여유를 둔다.
const expiryMarginMs = 30_000;

const logger = createLogger("auth/sessionStorage");

export function loadPersistedAuthSession(now: number = Date.now()): AuthSession | null {
  const raw = readRawSession();
  if (raw === null) {
    return null;
  }

  const session = parseAuthSession(raw);
  if (session === null) {
    // 파싱 실패한 저장된 값은 다음 init에서 또 시도하지 않도록 즉시 비운다.
    clearPersistedAuthSession();
    return null;
  }

  if (isExpired(session, now)) {
    clearPersistedAuthSession();
    return null;
  }

  return session;
}

export function persistAuthSession(session: AuthSession): void {
  try {
    window.localStorage.setItem(authSessionStorageKey, JSON.stringify(session));
  } catch (error) {
    // 저장 실패는 로그인 자체를 막지 않는다. 새로고침 시 로그인 유지만 안 될 뿐.
    logger.warn("Failed to persist auth session.", error);
  }
}

export function clearPersistedAuthSession(): void {
  try {
    window.localStorage.removeItem(authSessionStorageKey);
  } catch {
    // 저장소가 막힌 환경에서는 지울 값도 없다.
  }
}

export function isAuthSessionStorageEvent(event: StorageEvent): boolean {
  return event.key === null || event.key === authSessionStorageKey;
}

function readRawSession(): string | null {
  try {
    return window.localStorage.getItem(authSessionStorageKey);
  } catch {
    return null;
  }
}

// 저장소 값은 외부 입력으로 취급한다 — 모양이 하나라도 어긋나면 세션이 아니다.
function parseAuthSession(raw: string): AuthSession | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }

  const candidate = parsed as Record<string, unknown>;

  const provider = candidate.provider;
  if (
    typeof provider !== "string" ||
    !enabledAuthProviders.includes(provider as EnabledAuthProviderKind)
  ) {
    return null;
  }

  const subject = candidate.subject;
  const displayName = candidate.displayName;
  const accessToken = candidate.accessToken;
  if (
    typeof subject !== "string" ||
    typeof displayName !== "string" ||
    typeof accessToken !== "string"
  ) {
    return null;
  }
  if (subject === "" || accessToken === "") {
    return null;
  }

  const username = candidate.username;
  if (username !== null && typeof username !== "string") {
    return null;
  }

  const email = candidate.email;
  if (email !== null && typeof email !== "string") {
    return null;
  }

  const accessTokenExpiresAt = candidate.accessTokenExpiresAt;
  if (accessTokenExpiresAt !== null && typeof accessTokenExpiresAt !== "string") {
    return null;
  }
  if (typeof accessTokenExpiresAt === "string" && Number.isNaN(Date.parse(accessTokenExpiresAt))) {
    return null;
  }

  return {
    provider: provider as EnabledAuthProviderKind,
    subject,
    username,
    displayName,
    email,
    accessToken,
    accessTokenExpiresAt,
  };
}

function isExpired(session: AuthSession, now: number): boolean {
  if (session.accessTokenExpiresAt === null) {
    return false;
  }
  const expiresAtMs = Date.parse(session.accessTokenExpiresAt);
  if (Number.isNaN(expiresAtMs)) {
    return true;
  }
  return expiresAtMs - expiryMarginMs <= now;
}
